<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useCustomerConfig } from '@peanuds/app-shell';
import PreOrderDialog from './PreOrderDialog.vue';
import defaultVideoSrc from '@peanuds/ui/assets/media/cards-slide.mp4';

const videoRef = ref<HTMLVideoElement | null>(null);
const showPreOrderDialog = ref(false);
const { media } = useCustomerConfig();

const resolvedPromoMedia = computed(() => {
  return media.value.totalBalancePromoMedia ?? { src: defaultVideoSrc, type: 'video' as const };
});

const playVideo = () => {
  if (!videoRef.value || resolvedPromoMedia.value.type !== 'video') {
    return;
  }

  videoRef.value.play().catch(() => {
    // Ignore autoplay errors
  });
};

// Handle video click - open pre-order dialog
const handleVideoClick = () => {
  showPreOrderDialog.value = true;
};

onMounted(() => {
  playVideo();
});

watch(
  () => resolvedPromoMedia.value,
  async () => {
    if (resolvedPromoMedia.value.type !== 'video') {
      return;
    }

    await nextTick();
    playVideo();
  },
  { deep: true },
);
</script>

<template>
  <div class="promo-area">
    <div class="promo-area__media-wrapper" @click="handleVideoClick">
      <video
        v-if="resolvedPromoMedia.type === 'video'"
        ref="videoRef"
        class="promo-area__media"
        :src="resolvedPromoMedia.src"
        loop
        muted
        playsinline
      />
      <img v-else class="promo-area__media" :src="resolvedPromoMedia.src" alt="Pre-order Card" />
      <div class="promo-area__overlay" />
      <div class="promo-area__label">Pre-order Card</div>
    </div>
    <PreOrderDialog :visible="showPreOrderDialog" @update:visible="showPreOrderDialog = $event" />
  </div>
</template>

<style lang="scss" scoped>
.promo-area {
  display: flex;
  width: 100%;
  height: 110px;

  &__media-wrapper {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      transform: scale(1.01);
    }
  }

  &__media {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    border-radius: $radius-lg;
    border: 1px solid var(--surface-0, #fff);
  }

  &__overlay {
    position: absolute;
    inset: 0;
    border-radius: $radius-lg;
    opacity: 0.3;
    pointer-events: none;
    background: rgba(12, 126, 215, 0.49);
  }

  &__label {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 0 0.75rem 0.5rem 0;
    color: var(--text-color, #334155);
    font-size: 16px;
    font-weight: $font-medium;
    letter-spacing: var(--p-letter-spacing-body);
  }
}
</style>
